import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { PLAN_LIMITS, type ApiKeySummary, type PlanTier } from "@currencycore/shared";
import { listApiKeys, createApiKey, revokeApiKey, revealApiKey, getOrgUsage } from "../lib/api.ts";
import { toast } from "../components/Toast.tsx";
import { RowMenu } from "../components/RowMenu.tsx";
import { McpServerPanel } from "../components/McpServerPanel.tsx";
import { AiPanel } from "../components/AiPanel.tsx";
import { trackApiKeyAction, trackApiKeyRotate } from "../lib/analytics.ts";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null) {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function lastUsedBadge(iso: string | null) {
  if (!iso) return <span className="badge badge-neutral">never</span>;
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const label = mins < 5 ? "just now" : hrs < 1 ? `${mins}m ago` : hrs < 24 ? `${hrs}h ago` : `${days}d ago`;
  const cls = days > 7 ? "badge-warn" : "badge-ok";
  return <span className={`badge ${cls}`}><span className="dot" />{label}</span>;
}

// (row actions use the shared <RowMenu> component)

// ---------------------------------------------------------------------------
// Create-key modal
// ---------------------------------------------------------------------------

interface CreateKeyModalProps {
  orgId: string;
  orgPlan: PlanTier;
  planLoaded: boolean;
  onCreated: (fullKey: string, summary: ApiKeySummary) => void;
  onClose: () => void;
}

function CreateKeyModal({ orgId, orgPlan, planLoaded, onCreated, onClose }: CreateKeyModalProps) {
  const freeOrg = orgPlan === "free";
  const [name, setName] = useState("");
  // Until the org's real plan loads, don't assume "live": a paid org would flash
  // as Hobby (live disabled). Default to test, and keep the Live toggle disabled
  // (neutral) rather than guessing the wrong answer.
  const [env, setEnv] = useState<"live" | "test">(freeOrg ? "test" : "live");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await createApiKey(orgId, { name: name.trim(), environment: env });
      onCreated(res.key, res.summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create key.");
    } finally {
      setLoading(false);
    }
  }

  return createPortal(
    <div
      className="modal-root open"
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "block" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-scrim" style={{ position: "absolute", inset: 0, background: "oklch(8% 0.01 235 / 0.6)", backdropFilter: "blur(3px)" }} />
      <div className="modal" role="dialog" aria-modal="true" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 1 }}>
        <div className="modal-head">
          <div>
            <h3>Create API key</h3>
            <p className="sub">Live keys hit production; test keys are for development.</p>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="field">
              <label htmlFor="keyName">Key name</label>
              <input
                className="input"
                id="keyName"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. production-backend"
                autoComplete="off"
              />
              <span className="help">A label so you can recognise it later.</span>
            </div>
            <div className="field">
              <label>Environment</label>
              <div className="segmented" style={{ width: "fit-content" }}>
                <button
                  type="button"
                  className={env === "live" ? "active" : ""}
                  onClick={() => { if (planLoaded && !freeOrg) setEnv("live"); }}
                  disabled={!planLoaded || freeOrg}
                  title={!planLoaded ? "Checking your plan…" : freeOrg ? "Upgrade to a paid plan to issue live keys" : undefined}
                >
                  Live
                </button>
                <button type="button" className={env === "test" ? "active" : ""} onClick={() => setEnv("test")}>Test</button>
              </div>
              <span className="help">
                {!planLoaded
                  ? "Checking your plan to see whether live keys are available…"
                  : freeOrg
                    ? "Hobby organizations can create test keys only. Upgrade to issue live keys."
                    : env === "live"
                      ? `Live keys use this org's ${PLAN_LIMITS[orgPlan].label} rate limit.`
                      : `Test keys are rate-limited at the ${PLAN_LIMITS.free.label} tier (even in paid orgs).`}
              </span>
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><span className="spinner" />Creating…</> : "Create key"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Reveal-key modal
// ---------------------------------------------------------------------------

function RevealKeyModal({ fullKey, onClose }: { fullKey: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    void navigator.clipboard?.writeText(fullKey).catch(() => {});
    setCopied(true);
    toast("Secret copied to clipboard", true);
    setTimeout(() => setCopied(false), 2000);
  }

  return createPortal(
    <div
      className="modal-root open"
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "block" }}
    >
      <div className="modal-scrim" style={{ position: "absolute", inset: 0, background: "oklch(8% 0.01 235 / 0.6)", backdropFilter: "blur(3px)" }} />
      <div className="modal" role="dialog" aria-modal="true" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 1 }}>
        <div className="modal-head">
          <div>
            <h3>Save your secret key</h3>
            <p className="sub">Copy your key and store it somewhere safe. For security, we won't show it again.</p>
          </div>
        </div>
        <div className="modal-body">
          <div className="secret-box">
            <code style={{ flex: 1, minWidth: 0, overflowX: "auto", whiteSpace: "nowrap", color: "var(--accent-text)" }}>{fullKey}</code>
            <button className="btn btn-sm" onClick={copy}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h8" />
              </svg>
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="notice">
            <span className="ic" style={{ color: "var(--warn)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" />
              </svg>
            </span>
            Keep it in a secrets manager. CurrencyCore can't show or recover it later.
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Warning dialog (rotate / revoke)
// ---------------------------------------------------------------------------

interface ConfirmActionModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  busyLabel: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}

/** Shared warning dialog for the destructive key actions (rotate + revoke). */
function ConfirmActionModal({ title, message, confirmLabel, busyLabel, onConfirm, onClose }: ConfirmActionModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setLoading(true);
    setError(null);
    try {
      await onConfirm();
      // The caller closes the modal on success (and may open a follow-up modal).
    } catch (err) {
      setError(err instanceof Error ? err.message : "Action failed.");
      setLoading(false);
    }
  }

  return createPortal(
    <div
      className="modal-root open"
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "block" }}
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}
    >
      <div className="modal-scrim" style={{ position: "absolute", inset: 0, background: "oklch(8% 0.01 235 / 0.6)", backdropFilter: "blur(3px)" }} />
      <div className="modal" role="dialog" aria-modal="true" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 1 }}>
        <div className="modal-head">
          <div>
            <h3 style={{ color: "var(--danger)" }}>{title}</h3>
            <p className="sub">{message}</p>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose} disabled={loading} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
          </button>
        </div>
        {error && <div className="error-banner">{error}</div>}
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-danger" onClick={run} disabled={loading}>
            {loading ? <><span className="spinner" />{busyLabel}</> : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function DeveloperPage() {
  const { orgId } = useParams<{ orgId: string }>();
  // The org's real, authoritative plan comes from the API (org_subscriptions),
  // NOT the Better-Auth org object (which has no plan field). Live keys are
  // gated on this: anything other than the free/Hobby tier may issue live keys.
  const [orgPlan, setOrgPlan] = useState<PlanTier>("free");
  // The "free" default above is only a fallback; gating live keys on it before
  // the real plan loads would wrongly treat a paid org as Hobby. Track the load.
  const [orgPlanLoaded, setOrgPlanLoaded] = useState(false);
  const [keys, setKeys] = useState<ApiKeySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newFullKey, setNewFullKey] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  // A pending destructive action awaiting confirmation in the warning dialog.
  const [confirmAction, setConfirmAction] = useState<
    | { kind: "rotate" | "revoke"; key: ApiKeySummary }
    | null
  >(null);

  const fetchKeys = useCallback(() => {
    if (!orgId) return;
    setLoading(true);
    listApiKeys(orgId)
      .then(setKeys)
      .catch(() => setKeys([]))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  // Resolve the org's real plan tier from usage stats. `plan` is OrgPlan
  // (PlanTier | "custom"); custom plans are paid, so map them onto a paid tier
  // for gating + the rate-limit label (any non-"free" value enables live keys).
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    getOrgUsage(orgId)
      .then((u) => {
        if (!active) return;
        setOrgPlan(u.plan === "custom" ? "enterprise" : u.plan);
      })
      .catch(() => {})
      .finally(() => {
        if (active) setOrgPlanLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  function handleCreated(fullKey: string, summary: ApiKeySummary) {
    setShowCreate(false);
    setNewFullKey(fullKey);
    setKeys((prev) => [summary, ...prev]);
    trackApiKeyAction("create");
  }

  // Reveal the stored plaintext key and copy it to the clipboard. Only offered
  // for keys flagged `revealable` (created after the encrypt-at-rest change).
  async function handleCopy(key: ApiKeySummary) {
    if (!orgId) return;
    try {
      const fullKey = await revealApiKey(orgId, key.id);
      await navigator.clipboard.writeText(fullKey);
      toast("Key copied", true);
      trackApiKeyAction("copy");
    } catch {
      toast("Couldn't copy key", false);
    }
  }

  // Revoke is confirmed via the warning dialog; this runs on confirm.
  async function handleRevoke(key: ApiKeySummary) {
    if (!orgId) return;
    setRevoking(key.id);
    try {
      await revokeApiKey(orgId, key.id);
      setKeys((prev) => prev.filter((k) => k.id !== key.id));
      setConfirmAction(null);
      toast("Key revoked");
      trackApiKeyAction("revoke");
    } catch (err) {
      setRevoking(null);
      throw err; // surfaced in the dialog's error banner
    }
  }

  // Rotate = create a fresh key with the SAME name + environment, THEN revoke
  // the old one. Create first so a failure leaves the old key working. The new
  // plaintext key is shown once via the existing reveal flow (`handleCreated`).
  async function handleRotate(key: ApiKeySummary) {
    if (!orgId) return;
    const res = await createApiKey(orgId, { name: key.name, environment: key.environment });
    try {
      await revokeApiKey(orgId, key.id);
    } catch {
      // The new key exists; warn but don't block — the old key just lingers.
      toast("New key issued, but revoking the old key failed", false);
    }
    setConfirmAction(null);
    // Drop the old summary, prepend the new one, and reveal the new secret.
    setKeys((prev) => [res.summary, ...prev.filter((k) => k.id !== key.id)]);
    setNewFullKey(res.key);
    toast("Key rotated. New secret issued.", true);
    trackApiKeyRotate();
  }

  return (
    <div className="animate-fade-in">
      {/* Page header */}
      <div className="page-head rise">
        <div>
          <h1>API Keys</h1>
          <p className="sub">Secret keys authenticate requests from your servers. Treat them like passwords.</p>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Create key
          </button>
        </div>
      </div>

      {/* Info notice */}
      <div className="notice accent rise" style={{ marginBottom: "18px" }}>
        <span className="ic">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" /><path d="M12 8v4M12 16h.01" />
          </svg>
        </span>
        <span>
          A key's secret is shown <strong style={{ color: "var(--text)" }}>only once</strong> at creation.
          Store it in your secrets manager. You can always rotate or revoke it here.
        </span>
      </div>

      {/* Keys table */}
      {loading ? (
        <div className="skeleton-page">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: "4rem" }} />)}
        </div>
      ) : (
        <div className="card rise">
          <div className="card-head">
            <h2>Active keys</h2>
            <span className="hint mono">{keys.length} of 10 used</span>
          </div>
          {keys.length === 0 ? (
            <div className="card-pad" style={{ textAlign: "center", padding: "40px 20px" }}>
              <p className="muted">No API keys yet. Create one to start making requests.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Key</th>
                    <th>Created</th>
                    <th>Last used</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {keys.map((key) => (
                    <tr key={key.id} style={revoking === key.id ? { opacity: 0, transform: "translateX(8px)", transition: "opacity .3s, transform .3s" } : undefined}>
                      <td>
                        <span className="strong">{key.name}</span>
                        <div style={{ marginTop: "3px" }}>
                          <span className={`badge ${key.environment === "live" ? "badge-accent" : "badge-neutral"}`} style={{ textTransform: "capitalize", fontSize: "10px" }}>{key.environment}</span>
                        </div>
                      </td>
                      <td><span className="mono muted">{key.maskedKey}</span></td>
                      <td className="muted">{formatDate(key.createdAt)}</td>
                      <td>{lastUsedBadge(key.lastUsedAt)}</td>
                      <td className="col-right">
                        <RowMenu
                          ariaLabel="Key actions"
                          items={[
                            // Copy is only possible for keys we can decrypt
                            // (created after encrypt-at-rest). For older keys
                            // it's hidden — rotate to get a revealable key.
                            ...(key.revealable
                              ? [
                                  {
                                    label: "Copy key",
                                    onClick: () => { void handleCopy(key); },
                                    icon: (
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h8" />
                                      </svg>
                                    ),
                                  },
                                ]
                              : []),
                            {
                              label: "Rotate key",
                              onClick: () => setConfirmAction({ kind: "rotate", key }),
                              icon: (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5" />
                                </svg>
                              ),
                            },
                            {
                              label: "Revoke",
                              danger: true,
                              onClick: () => setConfirmAction({ kind: "revoke", key }),
                              icon: (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
                                </svg>
                              ),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Example usage */}
      <div className="card mt rise">
        <div className="card-head">
          <h2>Using your key</h2>
          <span className="hint">Pass it as a Bearer token</span>
        </div>
        <div className="card-pad">
          <pre className="mono" style={{ margin: 0, overflowX: "auto", fontSize: "12.5px", lineHeight: 1.7, color: "var(--text-muted)" }}>
{`curl https://api.currency-core.com/v1/convert \\
  -H `}<span style={{ color: "var(--accent-text)" }}>"Authorization: Bearer cc_live_••••4f2a"</span>{` \\
  -d '{ "from": "USD", "to": "EUR", "amount": 100 }'`}
          </pre>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "14px" }}>
            <Link to="../playground" className="btn btn-sm">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              Try it
            </Link>
            <span className="hint">Run live requests in the Playground. No key to copy.</span>
          </div>
        </div>
      </div>

      {/* MCP server — connect AI assistants; status, usage + enable/disable. */}
      <McpServerPanel orgId={orgId ?? ""} />

      {/* AI endpoint — natural-language currency queries; usage + enable/disable. */}
      <AiPanel orgId={orgId ?? ""} />

      {/* Modals */}
      {showCreate && orgId && (
        <CreateKeyModal
          orgId={orgId}
          orgPlan={orgPlan}
          planLoaded={orgPlanLoaded}
          onCreated={handleCreated}
          onClose={() => setShowCreate(false)}
        />
      )}
      {newFullKey && (
        <RevealKeyModal
          fullKey={newFullKey}
          onClose={() => setNewFullKey(null)}
        />
      )}
      {confirmAction?.kind === "rotate" && (
        <ConfirmActionModal
          title={`Rotate "${confirmAction.key.name}"`}
          message="Rotating immediately revokes this key and issues a new one. Apps using the current key will stop working until you update them."
          confirmLabel="Rotate key"
          busyLabel="Rotating…"
          onConfirm={() => handleRotate(confirmAction.key)}
          onClose={() => setConfirmAction(null)}
        />
      )}
      {confirmAction?.kind === "revoke" && (
        <ConfirmActionModal
          title={`Revoke "${confirmAction.key.name}"`}
          message="Revoking immediately disables this key. Any app using it will start getting 401s. This can't be undone."
          confirmLabel="Revoke key"
          busyLabel="Revoking…"
          onConfirm={() => handleRevoke(confirmAction.key)}
          onClose={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}
